import { useSyncExternalStore } from 'react';
import { useGuestOverride } from 'contexts/GuestOverrideContext';
import { isGuestSession, subscribeAuthChange } from 'utils/auth';

/**
 * Whether the current visitor is a guest (not logged in).
 *
 * Backed by `isGuestSession()` but made reactive via `useSyncExternalStore`:
 * when the session changes (e.g. a guest logs in and `storeAuthTokens` fires
 * `notifyAuthChange`), every consumer re-renders and re-reads guest state. This
 * is what lets the app switch guest→member layout in place, without a reload.
 *
 * Non-React code (ConnectionManager, RawWebSocketAdapter) keeps calling
 * `isGuestSession()` directly since hooks can't be used there.
 *
 * A GuestOverrideContext above the caller wins over the real session — the
 * Lobby Lab uses it to preview the member lobby (see contexts/
 * GuestOverrideContext). Outside such a provider this is purely the session.
 */
export const useGuest = (): boolean => {
  const session = useSyncExternalStore(subscribeAuthChange, isGuestSession, isGuestSession);
  const override = useGuestOverride();
  return override ?? session;
};

export default useGuest;
